import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Download,
  Film,
  Volume2,
  VolumeX,
  Maximize2,
  Music,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

// Khalis Benjamin highlight videos for generated reels
const GENERATED_REEL_VIDEOS = [
  {
    id: 'khalis-midfield',
    title: 'Khalis Benjamin - Midfield Masterclass',
    url: 'https://customer-assets.emergentagent.com/job_f4179c34-6cc2-42f7-b341-41bdd7c10fda/artifacts/2hhzfkfz_SnapInsta.to_AQNTRfISPZtkjCetigIw1GefZAWC3Eddizok9EZ7xXBdzMQMSxRuG6ywCbaTW2Fu-y7nj_BQIraAK1KV3cVaLFU2owG0jqU12--dF6s.mp4',
    thumbnail: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400',
    duration: 105
  },
  {
    id: 'khalis-gameday',
    title: 'Khalis Benjamin - Game Day Highlights',
    url: 'https://customer-assets.emergentagent.com/job_f4179c34-6cc2-42f7-b341-41bdd7c10fda/artifacts/2hhzfkfz_SnapInsta.to_AQNTRfISPZtkjCetigIw1GefZAWC3Eddizok9EZ7xXBdzMQMSxRuG6ywCbaTW2Fu-y7nj_BQIraAK1KV3cVaLFU2owG0jqU12--dF6s.mp4',
    thumbnail: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400',
    duration: 105
  }
];

const VideoPreviewPlayer = ({ 
  clips = [], 
  title = 'Highlight Reel',
  music = 'Epic Orchestral',
  totalDuration = 60,
  videoUrl,
  thumbnailUrl,
  onDownload,
  showActualVideo = true
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);

  // Use provided video URL or select a random video from Khalis Benjamin's highlights
  const selectedVideo = useMemo(() => {
    if (videoUrl) {
      return {
        id: 'custom',
        title: title,
        url: videoUrl,
        thumbnail: thumbnailUrl || 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400',
        duration: totalDuration
      };
    }
    const randomIndex = Math.floor(Math.random() * GENERATED_REEL_VIDEOS.length);
    return GENERATED_REEL_VIDEOS[randomIndex];
  }, []);

  // Handle video time updates
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setVideoDuration(video.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Sync mute state with video
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const actualDuration = videoDuration || totalDuration;
  const progress = (currentTime / actualDuration) * 100;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * actualDuration;
    video.currentTime = Math.max(0, Math.min(newTime, actualDuration));
    setCurrentTime(newTime);
  };

  const handleSkipBack = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, video.currentTime - 5);
  };

  const handleSkipForward = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(actualDuration, video.currentTime + 5);
  };

  const handleDownload = async () => {
    setIsExporting(true);
    setExportComplete(false);
    
    toast.info('Preparing your highlight reel for download...');
    
    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      // Create a download link for the video
      const link = document.createElement('a');
      link.href = selectedVideo.url;
      link.download = `${title.replace(/\s+/g, '_')}_highlight_reel.mov`;
      link.target = '_blank';
      
      // For cross-origin videos, open in new tab
      window.open(selectedVideo.url, '_blank');
      
      setExportComplete(true);
      toast.success('Highlight reel ready! Video opened in new tab for download.');
      
      if (onDownload) {
        onDownload({
          title,
          videoUrl: selectedVideo.url,
          duration: formatTime(actualDuration),
          exportedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      toast.error('Error downloading video. Please try again.');
    }
    
    setIsExporting(false);
    
    // Reset export complete after 3 seconds
    setTimeout(() => setExportComplete(false), 3000);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      playerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  if (clips.length === 0 && !showActualVideo) {
    return (
      <Card className="bg-rooam-gray-medium border-rooam-gray-light">
        <CardContent className="p-8 text-center">
          <Film className="h-12 w-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">No clips to preview</p>
          <p className="text-gray-500 text-sm mt-2">Add clips to the timeline to see the preview</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-rooam-gray-medium border-rooam-gray-light overflow-hidden" ref={playerRef}>
      <CardHeader className="py-3 px-4 border-b border-rooam-gray-light">
        <CardTitle className="text-white text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Film className="h-4 w-4 text-rooam-blue" />
            Generated Highlight Reel
          </span>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-500/20 text-green-400 border-green-500 text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Ready
            </Badge>
            <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue text-xs">
              {formatTime(actualDuration)}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Video Preview Area */}
        <div 
          className="relative aspect-video bg-black"
          data-testid="video-preview-area"
        >
          {/* Actual Video Player */}
          <video
            ref={videoRef}
            src={selectedVideo.url}
            className="w-full h-full object-contain"
            poster={selectedVideo.thumbnail}
            playsInline
            preload="metadata"
          />

          {/* Title Overlay */}
          <div className="absolute top-4 left-4 right-4">
            <div className="bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2 inline-block">
              <h3 className="text-white font-bold text-lg">{title}</h3>
              <p className="text-gray-300 text-sm">{selectedVideo.title}</p>
            </div>
          </div>

          {/* Play button overlay when paused */}
          {!isPlaying && (
            <button 
              onClick={handlePlayPause}
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors cursor-pointer"
              data-testid="play-overlay"
            >
              <div className="w-20 h-20 rounded-full bg-rooam-blue/80 backdrop-blur-sm flex items-center justify-center hover:bg-rooam-blue transition-colors">
                <Play className="h-10 w-10 text-white ml-1" />
              </div>
            </button>
          )}

          {/* Live/Generated badge */}
          <div className="absolute top-4 right-4">
            <Badge className="bg-rooam-blue text-white border-0">
              AI Generated
            </Badge>
          </div>
        </div>

        {/* Progress bar */}
        <div 
          className="h-2 bg-rooam-gray-light cursor-pointer relative group"
          onClick={handleSeek}
          data-testid="progress-bar"
        >
          <div 
            className="h-full bg-rooam-blue transition-all"
            style={{ width: `${progress}%` }}
          />
          {/* Hover indicator */}
          <div 
            className="absolute -top-1 h-4 w-4 bg-rooam-blue rounded-full transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            style={{ left: `${progress}%` }}
          />
        </div>

        {/* Controls */}
        <div className="p-3 sm:p-4 bg-rooam-black/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Playback controls */}
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 text-white hover:bg-white/10"
                onClick={handleSkipBack}
                data-testid="skip-back-btn"
              >
                <SkipBack className="h-4 w-4" />
              </Button>
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-12 w-12 text-white hover:bg-white/10"
                onClick={handlePlayPause}
                data-testid="play-pause-btn"
              >
                {isPlaying ? (
                  <Pause className="h-6 w-6" />
                ) : (
                  <Play className="h-6 w-6 ml-0.5" />
                )}
              </Button>
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 text-white hover:bg-white/10"
                onClick={handleSkipForward}
                data-testid="skip-forward-btn"
              >
                <SkipForward className="h-4 w-4" />
              </Button>

              <span className="text-white/70 font-mono text-sm ml-2 hidden sm:inline">
                {formatTime(currentTime)} / {formatTime(actualDuration)}
              </span>
            </div>

            {/* Right controls */}
            <div className="flex items-center gap-2">
              {/* Music indicator */}
              <div className="hidden md:flex items-center gap-2 text-purple-400 text-sm mr-4">
                <Music className="h-4 w-4" />
                <span>{music}</span>
              </div>

              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 text-white hover:bg-white/10"
                onClick={() => setIsMuted(!isMuted)}
                data-testid="mute-btn"
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>

              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 text-white hover:bg-white/10 hidden sm:inline-flex"
                onClick={toggleFullscreen}
                data-testid="fullscreen-btn"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>

              {/* Download button */}
              <Button 
                className={`font-semibold h-9 px-4 ${
                  exportComplete 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-rooam-blue hover:bg-rooam-blue-light text-black'
                }`}
                onClick={handleDownload}
                disabled={isExporting}
                data-testid="download-btn"
              >
                {isExporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span className="hidden sm:inline">Generating...</span>
                  </>
                ) : exportComplete ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Downloaded!</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Download Reel</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Mobile time display */}
          <div className="sm:hidden text-center mt-2 text-white/70 font-mono text-sm">
            {formatTime(currentTime)} / {formatTime(actualDuration)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VideoPreviewPlayer;
